import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { BaseQuery } from '@/hooks/baseQuery.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

type GetOrganizationByIdResponse = components['schemas']['GetOrganizationByIdEndpoint_GetOrganizationByIdResponse'];

export interface UseOrganizationByIdReturn extends BaseQuery {
  organization: GetOrganizationByIdResponse | null;
}

export function useOrganizationById(organizationId: number): UseOrganizationByIdReturn {
  const { getAccessToken } = useAuth();

  const getOrganizationById = async () => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}', {
      params: {
        path: { organizationId },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetOrganizationByIdResponse;
  };

  const query = useQuery({
    queryKey: [organizationId],
    queryFn: getOrganizationById,
    enabled: organizationId > 0,
    retry: false,
  });

  const { data, isLoading, error, refetch, isRefetching } = query;

  return {
    organization: data ?? null,
    isLoading,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
